import SnackBarComponent from '@efex-components/payables/addFundModal/components/CurrencySelector/SnackBarComponent';
import clientSvc from '@efex-services/client/clientSvc';
import { ContactStatus } from '@efex-services/client/types';
import { Button } from '@efexteam/components';
import theme from '@efexteam/mui-theme';
import { Actions, Can, Subjects, useRole } from '@efexteam/permission-manager';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  TableCell,
  useMediaQuery,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ClientsTableStyles from '../../ClientsTable.style';
import { ClientsTableRowActionProps } from '../../types';

const ClientRowAction = ({ client, onSelect }: ClientsTableRowActionProps) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { isPaymentAnalyst } = useRole();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isResendActivationEmail, setIsResendActivationEmail] = useState(false);
  const [resendType, setResendType] = useState<'success' | 'error'>('success');
  const [isSendingToken, setIsSendingToken] = useState(false);
  const open = Boolean(anchorEl);

  const { contactNaturalPersonEnterprise, contactStatus, id } = client;
  const { naturalPerson } = contactNaturalPersonEnterprise || {};
  const { email } = naturalPerson || {};

  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = () => {
    onSelect(client, 'Eliminar');
    handleClose();
  };

  const handleView = () => {
    onSelect(client, 'View');
    handleClose();
  };

  const handleResendActivationEmail = async (contactId: number) => {
    try {
      setIsSendingToken(true);
      await clientSvc.resendActivationEmail({ contactId });
      setResendType('success');
      setIsResendActivationEmail(true);
    } catch (error) {
      console.error(error);
      setResendType('error');
      setIsResendActivationEmail(true);
    } finally {
      setIsSendingToken(false);
      handleClose();
    }
  };

  if (contactStatus?.id === ContactStatus.DECLINED) {
    return null;
  }

  if (
    contactStatus?.id === ContactStatus.PENDING ||
    contactStatus?.id === ContactStatus.INACTIVE
  ) {
    return (
      <>
        <TableCell className={ClientsTableStyles.actionCell}>
          <Can action={Actions.CREATE} subject={Subjects.PAYMENTS}>
            <Button
              appearance="primary"
              className={ClientsTableStyles.actionButton}
              disabled={isSendingToken}
              onClick={() => handleResendActivationEmail(id)}
              startIcon={
                isSendingToken ? <CircularProgress size={16} /> : undefined
              }
            >
              {t('activateResend')}
            </Button>
          </Can>
        </TableCell>
        <SnackBarComponent
          isOpen={isResendActivationEmail}
          message={
            resendType === 'success'
              ? t('activateResendSuccess')
              : t('activateResendError')
          }
          severity={resendType}
          variant="filled"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          onClose={() => setIsResendActivationEmail(false)}
        />
      </>
    );
  }

  return (
    <TableCell className={ClientsTableStyles.actionCell}>
      <Can action={Actions.CREATE} subject={Subjects.PAYMENTS}>
        {isMobile ? (
          <IconButton
            type="button"
            aria-label="Send Payment"
            onClick={() => navigate(`/dashboard?sendPaymentTo=${email}`)}
          >
            <PaymentsOutlinedIcon color="primary" />
          </IconButton>
        ) : (
          <Button
            fullWidth
            className={ClientsTableStyles.actionButton}
            appearance="secondary"
            onClick={() => navigate(`/dashboard?sendPaymentTo=${email}`)}
          >
            {t(isPaymentAnalyst ? 'configureNewPayment' : 'makeNewPayment')}
          </Button>
        )}
      </Can>
      <Box className={ClientsTableStyles.menuBox}>
        <IconButton onClick={handleClickListItem}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          id={`menu-${id}`}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'lock-button',
            role: 'listbox',
          }}
        >
          <Can action={Actions.DELETE} subject={Subjects.BENEFICIARIES}>
            <MenuItem onClick={handleDelete}>
              <DeleteOutlineIcon color="error" />
            </MenuItem>
          </Can>
          <Can action={Actions.READ} subject={Subjects.BENEFICIARIES}>
            <MenuItem onClick={handleView}>
              <VisibilityIcon color="primary" />
            </MenuItem>
          </Can>
        </Menu>
      </Box>
    </TableCell>
  );
};

export default ClientRowAction;
